import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import { readFileSync } from 'fs';
import pathData from '../../../buildArtifacts/paths';
import Layout from '../../../components/Layout';
import PageCourse from '../../../components/PageCourse';
import PageCoursePropTypes from '../../../components/PageCourse/PageCoursePropTypes';
import tidySeo from '../../../utils/tidySeo';
import getSeoPageTitle from '../../../utils/getSeoPageTitle';
import getCoveoSearchMeta from '../../../utils/getCoveoSearchMeta';
import getCanonicalUrl from '../../../utils/getCanonicalUrl';
import getSettings from '../../../buildArtifacts/settings';

const { pageCourse: pageCoursePaths } = pathData;

export const pageCoursePropTypes = {
  pageData: PropTypes.shape(PageCoursePropTypes).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  seo: PropTypes.object,
  pageTitle: PropTypes.string,
  canonicalUrl: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  settingsGlobal: PropTypes.object.isRequired
};

function pageCourse({ pageData, seo, pageTitle, canonicalUrl, settingsGlobal }) {
  const {
    _id,
    _contentTypeId,
    _href,
    _as,
    slug,
    name,
    category,
    image,
    thumbnail,
    description,
    objectives,
    expectations,
    topics,
    jsonLdSchema,
    courseCompleteHeaderText,
    courseCompleteBody,
    courseCompleteImage
  } = pageData;

  if (_contentTypeId !== 'pageCourse') return null;

  return (
    <>
      <Layout seo={seo} showFooter pageTitle={pageTitle} canonicalUrl={canonicalUrl} settingsGlobal={settingsGlobal}>
        <PageCourse
          _id={_id}
          _contentTypeId={_contentTypeId}
          _href={_href}
          _as={_as}
          slug={slug}
          name={name}
          category={category}
          image={image}
          thumbnail={thumbnail}
          description={description}
          objectives={objectives}
          expectations={expectations}
          topics={topics}
          courseCompleteHeaderText={courseCompleteHeaderText}
          courseCompleteBody={courseCompleteBody}
          courseCompleteImage={courseCompleteImage}
        />

        {jsonLdSchema && !isEmpty(jsonLdSchema) && (
          // eslint-disable-next-line react/no-danger
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }} />
        )}
      </Layout>
    </>
  );
}

// This function gets called at build time
export const getStaticPaths = async ({ locales }) => ({
  paths: flatMap(locales, (locale) => [...pageCoursePaths].map((pathObject) => ({ ...pathObject, locale }))),
  fallback: false
});

export const getStaticProps = async ({ locale, params }) => {
  const settingsGlobal = getSettings(locale);
  const { slug } = params;

  const getPageData = () => {
    const fileData = readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageCourse/${slug}.json`, 'utf8');

    return JSON.parse(fileData);
  };

  const pageData = getPageData();

  const pageDataSeo = tidySeo(get(pageData, 'seo', {}));
  const pageCoveoSearchMeta = getCoveoSearchMeta(pageData, locale);
  const seo = { ...pageCoveoSearchMeta, ...pageDataSeo };
  seo.content_type = { name: 'content_type', value: pageData._contentTypeId };
  seo.lang = { name: 'lang', value: locale }

  const pageTitle = getSeoPageTitle(seo, pageData);
  delete seo.title;

  const canonicalUrl = getCanonicalUrl(pageData, locale, `self-guided-learning/${slug}`);

  return {
    props: {
      pageData,
      seo,
      pageTitle,
      canonicalUrl,
      settingsGlobal
    }
  };
};

pageCourse.propTypes = pageCoursePropTypes;

pageCourse.defaultProps = {
  seo: {},
  pageTitle: ''
};

export default pageCourse;
